import { useEffect, useMemo, useRef, useState } from "react";
import type { MessageDTO, MessagesSummary } from "@tw-pulse/shared";
import { api, ApiClientError } from "@/lib/api";
import { useConnectionContext } from "@/lib/connectionContext";
import { useInfiniteScroll } from "@/lib/useInfiniteScroll";
import { Button } from "@/components/ui/Button";
import { SummaryCards, type SummaryCard } from "@/components/SummaryCards";
import { ExternalLinkIcon, RefreshIcon } from "@/lib/icons";

type SummaryState =
  | { status: "loading" }
  | { status: "ready"; data: MessagesSummary }
  | { status: "error"; message: string };

type StatusFilter = "all" | "delivered" | "sent" | "undelivered" | "failed" | "queued" | "received";

type State =
  | { status: "loading" }
  | { status: "ready"; rows: MessageDTO[]; nextCursor: string | null }
  | { status: "error"; message: string };

const PAGE_SIZE = 25;

/**
 * Messages browser. Server-side cursor pagination over (dateCreated DESC,
 * _id DESC) — message volume is too high to client-paginate. Honors the
 * global account-context selector and supports status / search filters.
 *
 * Read-only at MVP; row drill-out goes to Twilio Console.
 */
export function MessagesPage() {
  const ctx = useConnectionContext();
  const scope = ctx.allSelected ? undefined : ctx.selectedIds;
  const scopeKey = scope ? scope.join(",") : "all";

  const [state, setState] = useState<State>({ status: "loading" });
  const [summary, setSummary] = useState<SummaryState>({ status: "loading" });
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [searchInput, setSearchInput] = useState("");
  const [searchApplied, setSearchApplied] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);

  // Summary refetches only on scope change — filters/search don't affect the
  // headline 24h window stats.
  useEffect(() => {
    if (ctx.status !== "ready") return;
    let cancelled = false;
    setSummary({ status: "loading" });
    void api.messages
      .summary(scope)
      .then((data) => {
        if (!cancelled) setSummary({ status: "ready", data });
      })
      .catch((err) => {
        if (!cancelled) {
          setSummary({
            status: "error",
            message: err instanceof ApiClientError ? err.message : "Failed to load",
          });
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.status, scopeKey]);

  // Debounce the search input so we don't fire a request per keystroke.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setSearchApplied(searchInput.trim());
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  // Fresh fetch whenever scope, status filter, or search term changes.
  // We bump a refetch token via state-key dependencies; cursors are reset.
  const reqIdRef = useRef(0);
  useEffect(() => {
    if (ctx.status !== "ready") return;
    const reqId = ++reqIdRef.current;
    setState({ status: "loading" });

    void api.messages
      .list(scope, {
        status: statusFilter === "all" ? undefined : statusFilter,
        search: searchApplied || undefined,
        limit: PAGE_SIZE,
      })
      .then((res) => {
        if (reqIdRef.current !== reqId) return; // a newer request superseded
        setState({
          status: "ready",
          rows: res.messages,
          nextCursor: res.nextCursor,
        });
      })
      .catch((err) => {
        if (reqIdRef.current !== reqId) return;
        setState({
          status: "error",
          message: err instanceof ApiClientError ? err.message : "Failed to load",
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.status, scopeKey, statusFilter, searchApplied]);

  function loadMore() {
    if (state.status !== "ready" || !state.nextCursor || loadingMore) return;
    const cursor = state.nextCursor;
    const reqId = reqIdRef.current; // freeze to detect superseding refetch
    setLoadingMore(true);
    void api.messages
      .list(scope, {
        status: statusFilter === "all" ? undefined : statusFilter,
        search: searchApplied || undefined,
        limit: PAGE_SIZE,
        cursor,
      })
      .then((res) => {
        if (reqIdRef.current !== reqId) return;
        setState((prev) =>
          prev.status === "ready"
            ? {
                status: "ready",
                rows: [...prev.rows, ...res.messages],
                nextCursor: res.nextCursor,
              }
            : prev,
        );
      })
      .catch(() => {
        // Soft-fail "load more" — keep the existing rows visible.
      })
      .finally(() => {
        if (reqIdRef.current === reqId) setLoadingMore(false);
      });
  }

  function refresh() {
    if (ctx.status !== "ready") return;
    // Bump request id to force a re-fetch even with identical inputs.
    setSearchApplied((s) => s);
    setState({ status: "loading" });
    const reqId = ++reqIdRef.current;
    void api.messages
      .list(scope, {
        status: statusFilter === "all" ? undefined : statusFilter,
        search: searchApplied || undefined,
        limit: PAGE_SIZE,
      })
      .then((res) => {
        if (reqIdRef.current !== reqId) return;
        setState({
          status: "ready",
          rows: res.messages,
          nextCursor: res.nextCursor,
        });
      })
      .catch((err) => {
        if (reqIdRef.current !== reqId) return;
        setState({
          status: "error",
          message: err instanceof ApiClientError ? err.message : "Failed to load",
        });
      });
  }

  const connNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of ctx.connections) {
      map.set(c.id, c.friendlyName ?? c.accountSid);
    }
    return map;
  }, [ctx.connections]);

  const filtersActive = statusFilter !== "all" || searchApplied.length > 0;

  // Auto-load next page when the sentinel scrolls into view.
  const hasMore = state.status === "ready" && !!state.nextCursor;
  const sentinelRef = useInfiniteScroll<HTMLDivElement>({
    enabled: hasMore && !loadingMore,
    onLoadMore: loadMore,
  });

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-brand-600 dark:text-brand-400">
            Activity
          </p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
            Messages
          </h1>
          <p className="mt-1 text-[13.5px] text-zinc-600 dark:text-zinc-400">
            Every SMS, MMS, and WhatsApp message synced from your connected Twilio accounts. Newest
            first.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={refresh}
          disabled={ctx.status !== "ready" || state.status === "loading"}
        >
          <RefreshIcon size={14} />
          Refresh
        </Button>
      </header>

      <SummaryCards
        loading={summary.status === "loading"}
        error={summary.status === "error" ? summary.message : null}
        cards={summary.status === "ready" ? buildMessagesSummaryCards(summary.data) : []}
      />

      <Toolbar
        search={searchInput}
        onSearchChange={setSearchInput}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        disabled={state.status === "loading"}
      />

      {state.status === "loading" && <SkeletonRows />}
      {state.status === "error" && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {state.message}
        </p>
      )}
      {state.status === "ready" && state.rows.length === 0 && !filtersActive && <EmptyState />}
      {state.status === "ready" && state.rows.length === 0 && filtersActive && (
        <FilteredEmpty
          onClear={() => {
            setSearchInput("");
            setSearchApplied("");
            setStatusFilter("all");
          }}
        />
      )}
      {state.status === "ready" && state.rows.length > 0 && (
        <>
          <Table rows={state.rows} connNameById={connNameById} />
          {/* Auto-load sentinel + footer status. */}
          <div ref={sentinelRef} className="flex justify-center pt-2">
            {state.nextCursor ? (
              <p className="text-[12px] text-zinc-500">
                {loadingMore ? (
                  <>Loading more…</>
                ) : (
                  <>Showing {state.rows.length.toLocaleString("en-US")} · scroll for more</>
                )}
              </p>
            ) : (
              <p className="text-[12px] text-zinc-500">
                {state.rows.length.toLocaleString("en-US")} message
                {state.rows.length === 1 ? "" : "s"} · end of results
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "delivered", label: "Delivered" },
  { value: "sent", label: "Sent" },
  { value: "undelivered", label: "Undelivered" },
  { value: "failed", label: "Failed" },
  { value: "queued", label: "Queued" },
  { value: "received", label: "Received" },
];

function Toolbar({
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  disabled,
}: {
  search: string;
  onSearchChange: (s: string) => void;
  statusFilter: StatusFilter;
  onStatusFilterChange: (s: StatusFilter) => void;
  disabled: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <input
        type="search"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Search by To or From number"
        className="h-9 min-w-[220px] max-w-md flex-1 rounded-md border border-zinc-200 bg-white px-3 text-[13px] text-zinc-900 placeholder:text-zinc-400 outline-none transition focus-visible:border-brand-500 focus-visible:bg-white focus-visible:shadow-ring dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100 dark:placeholder:text-zinc-600 dark:focus-visible:border-brand-500/70 dark:focus-visible:bg-zinc-950/60"
      />
      <div
        role="radiogroup"
        aria-label="Status filter"
        className="inline-flex flex-wrap items-center gap-0.5 rounded-md border border-zinc-200 bg-zinc-50 p-0.5 dark:border-zinc-800 dark:bg-zinc-900/40"
      >
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.value}
            type="button"
            role="radio"
            aria-checked={statusFilter === s.value}
            disabled={disabled}
            onClick={() => onStatusFilterChange(s.value)}
            className={
              "rounded-[5px] px-2.5 py-1 text-[12px] font-medium transition " +
              (statusFilter === s.value
                ? "bg-white text-zinc-900 shadow-sm dark:bg-zinc-800 dark:text-zinc-50"
                : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100")
            }
          >
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Table({ rows, connNameById }: { rows: MessageDTO[]; connNameById: Map<string, string> }) {
  return (
    <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/[0.02]">
      <table className="w-full text-left text-sm">
        <thead className="bg-zinc-50 text-[11px] font-medium uppercase tracking-wider dark:bg-zinc-900/60 text-zinc-500">
          <tr>
            <th className="px-4 py-2.5">When</th>
            <th className="px-4 py-2.5">Status</th>
            <th className="hidden px-4 py-2.5 md:table-cell">From → To</th>
            <th className="px-4 py-2.5">Body</th>
            <th className="hidden px-4 py-2.5 lg:table-cell">Account</th>
            <th className="hidden px-4 py-2.5 text-right md:table-cell">Cost</th>
            <th className="px-4 py-2.5 text-right" aria-label="External link" />
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800/80">
          {rows.map((m) => {
            const connName = connNameById.get(m.connectionId) ?? "—";
            return (
              <tr key={m.id} className="transition hover:bg-zinc-50 dark:hover:bg-zinc-900/60">
                <td className="px-4 py-3 align-top">
                  <div className="text-[12.5px] text-zinc-900 dark:text-zinc-100">
                    {formatRelative(m.dateCreated)}
                  </div>
                  <div className="font-mono text-[10.5px] text-zinc-400 dark:text-zinc-600">
                    {m.id}
                  </div>
                </td>
                <td className="px-4 py-3 align-top">
                  <StatusPill status={m.status} />
                  {m.errorCode !== null && (
                    <div className="mt-1 text-[10.5px] text-rose-700 dark:text-rose-300/80">
                      {m.errorCode}
                      {m.errorMessage ? ` · ${truncate(m.errorMessage, 40)}` : ""}
                    </div>
                  )}
                </td>
                <td className="hidden px-4 py-3 align-top md:table-cell">
                  <div className="font-mono text-[12px] text-zinc-700 dark:text-zinc-300">
                    {m.from ?? "—"}
                  </div>
                  <div className="font-mono text-[12px] text-zinc-500">→ {m.to ?? "—"}</div>
                  {m.direction && (
                    <div className="mt-0.5 text-[10.5px] uppercase tracking-wider text-zinc-400 dark:text-zinc-600">
                      {m.direction}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 align-top">
                  <div className="max-w-[28ch] truncate text-[12.5px] text-zinc-700 dark:text-zinc-300">
                    {m.body || (m.numMedia > 0 ? `[${m.numMedia} media]` : "—")}
                  </div>
                  {m.numMedia > 0 && (m.body?.length ?? 0) > 0 && (
                    <div className="text-[10.5px] text-zinc-400 dark:text-zinc-600">
                      + {m.numMedia} media
                    </div>
                  )}
                </td>
                <td className="hidden px-4 py-3 align-top text-[12px] text-zinc-600 dark:text-zinc-400 lg:table-cell">
                  {connName}
                </td>
                <td className="hidden px-4 py-3 align-top text-right md:table-cell">
                  {m.price ? (
                    <div className="font-mono text-[12px] text-zinc-800 dark:text-zinc-200">
                      {formatPrice(m.price, m.priceUnit)}
                    </div>
                  ) : (
                    <span className="text-[12px] text-zinc-400 dark:text-zinc-600">—</span>
                  )}
                </td>
                <td className="px-4 py-3 align-top text-right">
                  <a
                    href={twilioConsoleUrl(m)}
                    target="_blank"
                    rel="noreferrer"
                    title="Open in Twilio Console"
                    aria-label={`Open ${m.id} in Twilio Console`}
                    className="inline-flex items-center gap-1 text-[12px] text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
                  >
                    Console
                    <ExternalLinkIcon size={12} />
                  </a>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------

function StatusPill({ status }: { status: string | null }) {
  if (!status) {
    return <span className="text-[11.5px] text-zinc-500">—</span>;
  }
  const emerald = {
    bg: "bg-emerald-50 dark:bg-emerald-500/10",
    ring: "ring-emerald-500/30",
    text: "text-emerald-700 dark:text-emerald-300",
    dot: "bg-emerald-500 dark:bg-emerald-400",
  };
  const sky = {
    bg: "bg-sky-50 dark:bg-sky-500/10",
    ring: "ring-sky-500/30",
    text: "text-sky-700 dark:text-sky-300",
    dot: "bg-sky-500 dark:bg-sky-400",
  };
  const violet = {
    bg: "bg-violet-50 dark:bg-violet-500/10",
    ring: "ring-violet-500/30",
    text: "text-violet-700 dark:text-violet-300",
    dot: "bg-violet-500 dark:bg-violet-400",
  };
  const amber = {
    bg: "bg-amber-50 dark:bg-amber-500/10",
    ring: "ring-amber-500/30",
    text: "text-amber-700 dark:text-amber-300",
    dot: "bg-amber-500 dark:bg-amber-400",
  };
  const rose = {
    bg: "bg-rose-50 dark:bg-rose-500/10",
    ring: "ring-rose-500/30",
    text: "text-rose-700 dark:text-rose-300",
    dot: "bg-rose-500 dark:bg-rose-400",
  };
  const zincMuted = {
    bg: "bg-zinc-100 dark:bg-zinc-800/60",
    ring: "ring-zinc-300 dark:ring-zinc-700",
    text: "text-zinc-600 dark:text-zinc-400",
    dot: "bg-zinc-400 dark:bg-zinc-500",
  };
  const styles: Record<string, typeof emerald> = {
    delivered: emerald,
    sent: sky,
    received: violet,
    queued: amber,
    sending: amber,
    accepted: amber,
    scheduled: zincMuted,
    undelivered: rose,
    failed: rose,
    canceled: zincMuted,
  };
  const s = styles[status] ?? zincMuted;
  return (
    <span
      className={
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset " +
        s.bg +
        " " +
        s.ring +
        " " +
        s.text
      }
    >
      <span className={"h-1.5 w-1.5 rounded-full " + s.dot} />
      {status}
    </span>
  );
}

function SkeletonRows() {
  return (
    <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none">
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center justify-between border-b border-zinc-200 px-4 py-3 last:border-b-0 dark:border-zinc-800/80"
        >
          <div className="space-y-2">
            <div className="h-3 w-24 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
            <div className="h-2 w-44 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
          </div>
          <div className="flex gap-2">
            <div className="h-4 w-16 animate-pulse rounded-full bg-zinc-200 dark:bg-zinc-800/70" />
            <div className="hidden h-4 w-32 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70 md:block" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900/30 p-10 text-center">
      <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">No messages yet</h2>
      <p className="mx-auto mt-1.5 max-w-md text-[13px] text-zinc-600 dark:text-zinc-400">
        Messages appear here within a few seconds of your first sync. If you've just connected an
        account, give it a moment and refresh.
      </p>
    </div>
  );
}

function FilteredEmpty({ onClear }: { onClear: () => void }) {
  return (
    <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900/30 p-6 text-center">
      <p className="text-[13px] text-zinc-600 dark:text-zinc-400">
        No messages match the current filter.
      </p>
      <button
        type="button"
        onClick={onClear}
        className="mt-2 text-[12.5px] text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
      >
        Clear filter
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Twilio Console doesn't expose a per-message deep link in the public site
 * (the messaging logs view filters by SID via query string instead).
 */
function twilioConsoleUrl(m: MessageDTO): string {
  return `https://console.twilio.com/us1/monitor/logs/sms?frameUrl=%2Fconsole%2Fsms%2Flogs%2F${encodeURIComponent(m.id)}`;
}

function formatRelative(iso: string | null): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const diffMs = Date.now() - then;
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  // Older: show a short calendar date.
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatPrice(price: string, unit: string | null): string {
  const n = Number(price);
  if (Number.isNaN(n)) return price;
  const abs = Math.abs(n);
  const sym = (unit ?? "USD").toUpperCase();
  return `${sym} ${abs.toFixed(abs < 1 ? 4 : 2)}`;
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : `${s.slice(0, n - 1)}…`;
}

function buildMessagesSummaryCards(s: MessagesSummary): SummaryCard[] {
  return [
    {
      label: "Messages · 24h",
      value: formatStat(s.total),
      hint:
        s.total === 0
          ? "Awaiting traffic"
          : `${formatStat(s.delivered)} delivered · ${formatStat(s.failed)} failed`,
    },
    {
      label: "Delivery rate · 24h",
      value: s.deliveryRate === null ? "—" : `${(s.deliveryRate * 100).toFixed(1)}%`,
      hint:
        s.deliveryRate === null
          ? "0 messages reached terminal state"
          : `${formatStat(s.delivered)} of ${formatStat(s.delivered + s.failed)}`,
    },
    {
      label: "Failed · 24h",
      value: formatStat(s.failed),
      hint: s.failed === 0 ? "All clear" : "failed + undelivered",
    },
    {
      label: "Active senders · 24h",
      value: formatStat(s.activeSenders),
      hint: s.activeSenders === 0 ? "—" : "Distinct outbound `from` values",
    },
  ];
}

function formatStat(n: number): string {
  return Math.round(n).toLocaleString("en-US");
}
